import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, ShieldAlert, ShieldCheck, XCircle } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const STATUS_VARIANTS = {
  verified: 'bg-emerald-100 text-emerald-800',
  unverified: 'bg-amber-100 text-amber-800',
  disputed: 'bg-rose-100 text-rose-800',
  removed: 'bg-slate-200 text-slate-700',
};

const DashboardCard = ({ label, value, icon: Icon, accent }) => (
  <Card>
    <CardContent className="flex items-center justify-between p-5">
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="mt-1 text-3xl font-semibold text-slate-900">{value}</p>
      </div>
      <span className={`rounded-full p-3 ${accent}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
    </CardContent>
  </Card>
);

export default function AdminWebsiteReviewPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filters, setFilters] = useState({ reviewStatus: 'attention', q: '' });

  const handleUnauthorized = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const fetchDashboard = async () => {
    const response = await axios.get(`${API}/admin/event-website-review/dashboard`, { headers: getAuthHeaders() });
    setDashboard(response.data);
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.reviewStatus && filters.reviewStatus !== 'all') params.append('reviewStatus', filters.reviewStatus);
      if (filters.q.trim()) params.append('q', filters.q.trim());
      const response = await axios.get(`${API}/admin/event-website-review/list?${params.toString()}`, { headers: getAuthHeaders() });
      setEvents(response.data.events || []);
    } finally {
      setLoading(false);
    }
  };

  const refreshPage = async () => {
    try {
      await Promise.all([fetchDashboard(), fetchEvents()]);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error('Failed to load website review queue');
      }
    }
  };

  useEffect(() => {
    refreshPage();
  }, [filters.reviewStatus]);

  const updateReview = async (eventId, status) => {
    try {
      await axios.post(`${API}/admin/event-website-review/${eventId}`, { status }, { headers: getAuthHeaders() });
      toast.success(`Review status updated to ${status}`);
      refreshPage();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update review status');
      }
    }
  };

  const runAiReview = async () => {
    try {
      setRunning(true);
      await axios.post(`${API}/admin/event-website-review/run-now?limit=25`, {}, { headers: getAuthHeaders() });
      toast.success('AI website review started');
      window.setTimeout(refreshPage, 4000);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start AI review');
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-website-review-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Event website review</h1>
            <p className="mt-1 max-w-3xl text-sm leading-7 text-slate-600">
              Review governed external website states, promote verified links, and inspect listings hidden by the daily AI verification job.
            </p>
          </div>
          <Button onClick={runAiReview} disabled={running} className="bg-orange-600 hover:bg-orange-700" data-testid="run-ai-website-review-button">
            <RefreshCw className={`mr-2 h-4 w-4 ${running ? 'animate-spin' : ''}`} />
            Run AI review now
          </Button>
        </div>

        {dashboard && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <DashboardCard label="Needs attention" value={dashboard.counts.attention} icon={AlertTriangle} accent="bg-amber-100 text-amber-700" />
            <DashboardCard label="Verified" value={dashboard.counts.verified} icon={ShieldCheck} accent="bg-emerald-100 text-emerald-700" />
            <DashboardCard label="Unverified" value={dashboard.counts.unverified} icon={ShieldAlert} accent="bg-amber-100 text-amber-700" />
            <DashboardCard label="Disputed" value={dashboard.counts.disputed} icon={XCircle} accent="bg-rose-100 text-rose-700" />
            <DashboardCard label="Removed" value={dashboard.counts.removed} icon={AlertTriangle} accent="bg-slate-200 text-slate-700" />
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>Focus the moderation queue without editing raw event records.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-[220px,1fr]">
            <Select value={filters.reviewStatus} onValueChange={(value) => setFilters((prev) => ({ ...prev, reviewStatus: value }))}>
              <SelectTrigger data-testid="website-review-status-filter">
                <SelectValue placeholder="Filter by review status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attention">Needs attention</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
                <SelectItem value="disputed">Disputed</SelectItem>
                <SelectItem value="removed">Removed</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-3">
              <Input
                value={filters.q}
                onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                placeholder="Search event, host or URL"
                data-testid="website-review-search-input"
              />
              <Button variant="outline" onClick={fetchEvents} data-testid="website-review-apply-filters-button">Apply</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading moderation queue...</div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No events match the current filters.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {events.map((event) => {
                  const review = event.eventWebsiteReview || {};
                  const detailPath = `/events/${event.citySlug}/${event.venueSlug}/${event.slug}`;

                  return (
                    <div key={event.id} className="grid gap-4 p-5 xl:grid-cols-[1.4fr,0.9fr,0.9fr]" data-testid={`website-review-row-${event.slug}`}>
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="text-lg font-semibold text-slate-900">{event.name}</h2>
                          <Badge className={STATUS_VARIANTS[review.status] || STATUS_VARIANTS.unverified} data-testid={`website-review-status-${event.slug}`}>
                            {review.status || 'unverified'}
                          </Badge>
                          {event.websiteReviewHold?.hiddenByAi && <Badge variant="outline">Hidden for review</Badge>}
                        </div>
                        <p className="text-sm text-slate-500">{event.venueName}, {event.city}</p>
                        <p className="text-sm text-slate-600 break-all">{review.destinationHostname || review.destinationUrl || 'No public destination URL'}</p>
                        <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                          <span>Last checked: {review.lastCheckedAt ? new Date(review.lastCheckedAt).toLocaleString('en-GB') : 'Never'}</span>
                          <span>Changed by: {review.lastChangedBy || 'system'}</span>
                        </div>
                        {review.auditNote && <p className="text-sm leading-6 text-slate-600">{review.auditNote}</p>}
                      </div>

                      <div className="space-y-3 text-sm text-slate-600">
                        <div>
                          <p className="font-medium text-slate-900">Listing</p>
                          <a href={detailPath} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-2 text-orange-700 hover:text-orange-800">
                            Open event page
                            <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          </a>
                        </div>
                        {review.destinationUrl && (
                          <div>
                            <p className="font-medium text-slate-900">Destination URL</p>
                            <a href={review.destinationUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-2 break-all text-orange-700 hover:text-orange-800">
                              Visit destination
                              <ExternalLink className="h-4 w-4" aria-hidden="true" />
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 xl:justify-end">
                        <Button variant="outline" onClick={() => updateReview(event.id, 'verified')} data-testid={`website-review-verify-${event.slug}`}>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Mark verified
                        </Button>
                        <Button variant="outline" onClick={() => updateReview(event.id, 'disputed')} data-testid={`website-review-dispute-${event.slug}`}>
                          Mark disputed
                        </Button>
                        <Button variant="outline" onClick={() => updateReview(event.id, 'removed')} data-testid={`website-review-remove-${event.slug}`}>
                          Remove link
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}